/**
 * Helpers for rendering / comparing the structured specs we attach to
 * a quote option when it's picked from the disti listings picker.
 *
 * Lives in `utils/` rather than `components/quotes/` because the
 * picker dialog (`DistiListingSearchDialog`) and the quote editor
 * (`QuoteEditor`) both consume the formatter — keeping it in one
 * place avoids drift between the two surfaces.
 */

import type { ExtractedSpecs } from '@techgitter/contracts';

/**
 * Compact one-line summary, e.g.
 *   "Ultra 5-225U · 16GB DDR5 · 512GB NVMe SSD · 14" WUXGA · Win 11 Pro · LTE · Touch"
 *
 * Skips fields the extractor missed so a row with only "i7" + "16GB"
 * doesn't render placeholder slashes. Returns an empty string when
 * specs is null/undefined or no fields are populated.
 */
export function formatSpecSummary(
  specs: ExtractedSpecs | null | undefined,
): string {
  if (!specs) return '';
  const parts: string[] = [];
  if (specs.cpu) {
    parts.push(specs.cpu);
  } else if (specs.cpuSeries) {
    // Fallback when we have the tier but not the full model — e.g.
    // older feeds that only mention "i7" without a model number.
    parts.push(specs.cpuSeries);
  } else if (specs.cpuFamily) {
    // Last-resort: just the vendor brand (Intel / AMD / etc).
    parts.push(specs.cpuFamily);
  }
  if (specs.ramGb) {
    parts.push(specs.ramType ? `${specs.ramGb}GB ${specs.ramType}` : `${specs.ramGb}GB`);
  }
  if (specs.storageGb) {
    const size =
      specs.storageGb >= 1000 ? `${specs.storageGb / 1000}TB` : `${specs.storageGb}GB`;
    parts.push(specs.storageType ? `${size} ${specs.storageType}` : size);
  }
  if (specs.screenSizeIn) {
    const screenBits: string[] = [`${specs.screenSizeIn}"`];
    if (specs.resolution) screenBits.push(specs.resolution);
    parts.push(screenBits.join(' '));
  }
  if (specs.operatingSystem) {
    // "Windows 11 Pro" → "Win 11 Pro" — keeps the line tight.
    // macOS / ChromeOS pass through.
    parts.push(specs.operatingSystem.replace(/^Windows\b/, 'Win'));
  }
  if (specs.hasLte) parts.push('LTE');
  if (specs.hasWifi) parts.push('WiFi');
  if (specs.hasFingerprint) parts.push('FP');
  if (specs.hasBacklitKeyboard) parts.push('Backlit');
  if (specs.hasTouchscreen) parts.push('Touch');
  return parts.join(' · ');
}

/**
 * Returns true when the spec summary carries information the
 * description text doesn't already cover. Per-token check — case-
 * insensitive substring match against the description; if any token
 * is missing, the spec line "differs" and is worth showing.
 *
 * Hand-typed options (no sourceSpecs) and rows where the description
 * already contains every token (rare in practice — calc descriptions
 * use slightly different formatting from the spec line) return false
 * and the spec line stays hidden.
 */
export function specDiffersFromDescription(
  specs: ExtractedSpecs | null | undefined,
  description: string,
): boolean {
  const summary = formatSpecSummary(specs);
  if (!summary) return false;
  const desc = (description || '').toLowerCase();
  const tokens = summary
    .split(' · ')
    .map((t) => t.trim())
    .filter(Boolean);
  return tokens.some((t) => !desc.includes(t.toLowerCase()));
}
